class MobileGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isScrolling = false;
  }

  connectedCallback() {
    // Store original content before any modifications
    this.originalContent = this.innerHTML;
    this.init();
  }

  init() {
    // Only initialize on mobile
    if (window.innerWidth > 767) {
      this.destroy();
      return;
    }

    this.style.display = 'block';
    this.render();
    this.bindEvents();
    this.setActiveImage(0);
  }

  render() {
    // Don't re-render if already rendered
    if (this.querySelector('.mobile-gallery__main')) return;

    const mediaItems = Array.from(this.querySelectorAll('.media-item'));
    if (mediaItems.length === 0) return;

    // Create main image container
    const mainContainer = document.createElement('div');
    mainContainer.className = 'mobile-gallery__main';
    mainContainer.setAttribute('role', 'list');

    // Create thumbnails container
    const thumbsContainer = document.createElement('div');
    thumbsContainer.className = 'mobile-gallery__thumbs';
    thumbsContainer.setAttribute('role', 'list');

    // Process media items
    mediaItems.forEach((item, index) => {
      // Clone for main image
      const mainItem = item.cloneNode(true);
      mainItem.className = 'mobile-gallery__main-item';
      mainItem.setAttribute('role', 'listitem');
      mainItem.dataset.index = index;
      if (index === 0) mainItem.classList.add('active');
      mainContainer.appendChild(mainItem);

      // Create thumbnail
      const thumb = document.createElement('div');
      thumb.className = 'mobile-gallery__thumb';
      thumb.setAttribute('role', 'listitem');

      thumb.dataset.index = index;
      if (index === 0) thumb.classList.add('active');

      // Handle media content for thumbnail
      const media = item.querySelector('img, video');
      if (media) {
        if (media.tagName === 'VIDEO') {
          // For videos, use poster image or create a placeholder
          const thumbImg = document.createElement('img');
          if (media.poster) {
            thumbImg.src = media.poster;
            thumbImg.alt = media.alt || 'Video thumbnail';
          } else {
            // Fallback to a video icon or first frame
            thumbImg.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iNCIgZmlsbD0iIzMzMzMzMyIvPgo8cGF0aCBkPSJNMTYgMTJMMjggMjBMMTYgMjhWMTJaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
            thumbImg.alt = 'Video';
          }
          thumb.appendChild(thumbImg);
        } else {
          // For images, clone as before
          const thumbMedia = media.cloneNode(true);
          thumb.appendChild(thumbMedia);
        }
      }

      thumbsContainer.appendChild(thumb);
    });

    // Clear original content and append new structure
    this.innerHTML = '';
    this.appendChild(mainContainer);
    this.appendChild(thumbsContainer);
  }

  bindEvents() {
    // Thumbnail click events
    const thumbs = this.querySelectorAll('.mobile-gallery__thumb');
    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        this.setActiveImage(index);
      });
    });

    // Main image scroll events
    const mainContainer = this.querySelector('.mobile-gallery__main');
    if (mainContainer) {
      mainContainer.addEventListener('scroll', () => {
        if (this.isScrolling) return;

        const scrollLeft = mainContainer.scrollLeft;
        const itemWidth = mainContainer.clientWidth;
        const newIndex = Math.round(scrollLeft / itemWidth);

        if (newIndex !== this.currentIndex) {
          this.currentIndex = newIndex;
          this.updateActiveThumbnail();
        }
      });
    }

    // Handle resize to re-initialize if needed
    window.addEventListener('resize', () => {
      this.init();
    });
  }

  setActiveImage(index) {
    this.isScrolling = true;
    this.currentIndex = index;

    // Update main image
    const mainContainer = this.querySelector('.mobile-gallery__main');
    const targetScrollLeft = index * mainContainer.clientWidth;

    mainContainer.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });

    // Update active states
    this.updateActiveStates();

    // Reset scrolling flag after animation
    setTimeout(() => {
      this.isScrolling = false;
    }, 300);
  }

  updateActiveThumbnail() {
    const thumbs = this.querySelectorAll('.mobile-gallery__thumb');
    thumbs.forEach((thumb, index) => {
      thumb.classList.toggle('active', index === this.currentIndex);
    });

    // Scroll thumbnails if needed
    this.scrollThumbnailsToActive();
  }

  updateActiveStates() {
    // Update main images
    const mainItems = this.querySelectorAll('.mobile-gallery__main-item');
    mainItems.forEach((item, index) => {
      const isActive = index === this.currentIndex;
      
      // Toggle active class
      item.classList.toggle('active', isActive);

      // Set aria-hidden based on whether the item is active or not
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    // Update thumbnails
    this.updateActiveThumbnail();
  }

  scrollThumbnailsToActive() {
    const thumbsContainer = this.querySelector('.mobile-gallery__thumbs');
    const activeThumb = this.querySelector('.mobile-gallery__thumb.active');

    if (thumbsContainer && activeThumb) {
      const containerWidth = thumbsContainer.clientWidth;
      const thumbWidth = activeThumb.offsetWidth;
      const thumbLeft = activeThumb.offsetLeft;
      const scrollLeft = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);

      thumbsContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    }
  }

  destroy() {
    // Reset to original state for desktop
    if (this.originalContent) {
      this.innerHTML = this.originalContent;
    }
    this.style.display = 'contents';
  }
}

// Register the custom element
customElements.define('mobile-gallery', MobileGallery);